import { Box } from '@mui/material';
import BlockButton from 'components/@home/@messages/ChatPanel/MessagesList/MessageGroup/Blocks/BlockButton';
import BlockSlider from 'components/@home/@messages/ChatPanel/MessagesList/MessageGroup/Blocks/BlockSlider';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import messageShape from 'shapes/message';
import { CHAT_MARKDOWN_DISALLOWED_ELEMENTS } from 'utils/chatMarkdownDisallowedElements';
import remarkStripChatImages from 'utils/remarkStripChatImages';
import { preprocessWhatsAppMarkdown } from 'utils/remarkWhatsAppPreprocess';
import ChatMarkdownLink from './ChatMarkdownLink';

const Text = ({ text: { type, text }, messageId }) => {
  const source = typeof text === 'string' ? text : String(text ?? '');
  return type === 'mrkdwn' ? (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkBreaks, remarkStripChatImages]}
      disallowedElements={CHAT_MARKDOWN_DISALLOWED_ELEMENTS}
      components={{
        a: ({ href, children: anchorChildren }) => (
          <ChatMarkdownLink href={href} messageId={messageId}>
            {anchorChildren}
          </ChatMarkdownLink>
        ),
      }}
    >
      {preprocessWhatsAppMarkdown(source)}
    </ReactMarkdown>
  ) : (
    <p>{text}</p>
  );
};

const renderBlockButton = (blockId, element, msgId) => (
  <BlockButton blockId={blockId} button={element} msgId={msgId} key={blockId} />
);

const renderBlockSlider = (blockId, element, msgId) => (
  <BlockSlider blockId={blockId} slider={element} msgId={msgId} key={blockId} />
);

const renderActionBlockElements = (blockId, elements, msgId) => (
  <Box>
    {elements
      ? elements.map(element => {
          if (element.type === 'button') {
            return renderBlockButton(blockId, element, msgId);
          }
          if (element.type === 'slider') {
            return renderBlockSlider(blockId, element, msgId);
          }
          return null;
        })
      : null}
  </Box>
);

const renderSectionBlock = (block, msgId) => (
  <Box
    sx={theme => ({
      display: 'flex',
      flexDirection: 'row',
      gap: theme.spacing(1),
      alignItems: 'center',
    })}
  >
    <Box
      sx={theme => ({
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
      })}
    >
      {block.text ? (
        <Box
          sx={theme => ({
            marginBottom: theme.spacing(1),
            display: 'flex',
            flexDirection: 'column',
          })}
        >
          <Text text={block.text} messageId={msgId} />
        </Box>
      ) : null}
      {!block.fields || (
        <Box
          sx={theme => ({
            display: 'grid',
            gridTemplateColumns: 'repeat(2, max-content)',
            gridColumnGap: theme.spacing(1),
            gridRowGap: theme.spacing(0.5),
          })}
        >
          {block.fields.map((f, idx) => (
            <Text key={`${f}-${idx}`} text={f} messageId={msgId} />
          ))}
        </Box>
      )}
    </Box>
    {block.accessory ? (
      <Box
        sx={theme => ({
          flex: '0 0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(1),
        })}
      >
        {renderActionBlockElements(block.block_id, [block.accessory], msgId)}
      </Box>
    ) : null}
  </Box>
);

const renderDivider = () => <hr />;

const MessageBlocks = ({ message }) => {
  const renderBlock = {
    actions: b => renderActionBlockElements(b.block_id, b.elements, message._id),
    section: b => renderSectionBlock(b, message._id),
    divider: renderDivider,
  };

  return message.blocks?.length ? (
    <Box
      sx={theme => ({
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'stretch',
        marginTop: theme.spacing(1),
      })}
    >
      {message.blocks.map(block =>
        Object.prototype.hasOwnProperty.call(renderBlock, block.type)
          ? renderBlock[block.type](block)
          : null,
      )}
    </Box>
  ) : null;
};

MessageBlocks.propTypes = {
  message: messageShape.isRequired,
};

export default MessageBlocks;
